'use client';
import { Box, BoxProps, Button, InputProps, TextField, Typography } from '@mui/material';
import { IconSearch } from '@tabler/icons-react';
import { ChangeEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useId } from 'react';

const DEBOUNCE_DELAY = 300;

export interface SearchBoxDataProps {
  defaultValue?: string;
  resultsAreaId?: string;
  label?: string | ReactNode;
  onSearch: (_value: string) => void;
  setPage: (_page: number) => void;
  isSearching: boolean;
}

export interface SearchBoxPresentationProps extends Omit<InputProps, 'defaultValue'> {
  wrapperProps?: BoxProps;
}

export interface SearchBoxProps extends SearchBoxDataProps, SearchBoxPresentationProps {}

export function SearchBox({
  label = 'Search',
  defaultValue = '',
  wrapperProps,
  resultsAreaId = undefined,
  onSearch,
  isSearching,
}: SearchBoxProps) {
  const id = useId();
  const [mounted, setMounted] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(defaultValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Defer MUI TextField until after mount to avoid hydration mismatch (InputBase/div structure differs server vs client)
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      onSearch(term);
    },
    [onSearch, debounceRef]
  );

  const changeSearchValue = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const value = event?.target?.value || '';
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      setSearchValue(value);
      debounceRef.current = setTimeout(() => {
        handleSearch(value);
      }, DEBOUNCE_DELAY);
    },
    [debounceRef, handleSearch]
  );

  return (
    <>
      <Box
        data-component="SearchBox"
        {...wrapperProps}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'start',
          marginBottom: 2,
          gap: 2,
          ...wrapperProps?.sx,
        }}
      >
        {typeof label === 'string' ? (
          <Typography variant="medium" id={`search-label-${id}`}>
            {label}
          </Typography>
        ) : (
          label
        )}
        {mounted ? (
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Search"
            defaultValue={searchValue}
            onChange={changeSearchValue}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearch(searchValue);
              }
            }}
            size="small"
            slotProps={{
              htmlInput: {
                type: 'search',
                id: `search-input-${id}`,
                'aria-labelledby': `search-label-${id}`,
                ...(resultsAreaId ? { 'aria-controls': resultsAreaId } : {}),
              },
              input: {
                endAdornment: (
                  <>
                    <Button
                      sx={{ borderRadius: 10 }}
                      onClick={() => onSearch(searchValue)}
                      loading={isSearching}
                    >
                      <IconSearch />
                    </Button>
                  </>
                ),
              },
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 40,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
            aria-hidden
          />
        )}
      </Box>
    </>
  );
}
